import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { app } from "@microsoft/teams-js";
import { azureTables, STRINGS_EN, tableActions } from "../constant";
import { triggerOperation } from "../utils/triggerOpration";
import { getItem } from "../utils/localStorage";
import { keys } from "../constant/keyConfig";
import { getCookie } from "../utils/cookies";
import { isUserDBSelectionEnabled } from "../utils/common";
import { upsertUserDetails } from "../pages/ZycusChatV2/utils/upsertUserDetails";

const UserContext = createContext();

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};

export const UserContextProvider = ({ children }) => {
  const [userContext, setUserContext] = useState({});
  const [addAccountEmail, setAddAccountEmail] = useState("");
  const [loadingTeamsContext, setLoadingTeamsContext] = useState(true);
  const [errorTeamsContext, setErrorTeamsContext] = useState(null);
  const [theme, setTheme] = useState("default");
  const [v1V2Flag, setV1V2Flag] = useState(null);


  // Apply the theme to the DOM and dispatch an event
  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute("data-theme", theme);
      window.dispatchEvent(
        new CustomEvent("themeChanged", {
          detail: { theme },
        })
      );
    }
  }, [theme]);
  const contextfromLocalstore = () => {
    const contextInfo = getItem(keys.TEAMS_CONTEXT_KEY);
    if (!contextInfo) {
      return null;
    }
    const { email, aadObjectId } = contextInfo;

    if (!email || !aadObjectId) {
      return null;
    }
    const teamContextData = {
      id: aadObjectId,
      userPrincipalName: email,
      tenant: {
        id: "",
        teamsSku: "",
      },
    };

    return teamContextData;
  };
  const contextfromCookie = () => {
    const email = getCookie(keys.USEREMAIL);
    const primaryEmail = getCookie(keys.PRIMARYEMAIL);
    const aadObjectId = getCookie(keys.AADOBJECTID);

    if (!primaryEmail && !aadObjectId) {
      return null;
    }
    const teamContextData = {
      id: aadObjectId ? aadObjectId : "",
      userPrincipalName: email,
      primaryEmail: primaryEmail,
      tenant: {
        id: "",
        teamsSku: "",
      },
    };

    return teamContextData;
  };

  const updateTeamsContext = (data) => {
    const { email, aadObjectId } = data;
    const teamContextData = {
      id: aadObjectId,
      userPrincipalName: email,
      tenant: {
        id: "",
        teamsSku: "",
      },
    };
    setUserContext(teamContextData);
    setLoadingTeamsContext(false);
    setErrorTeamsContext(null);
  };

  const updateAccountEmail = (email) => {
    setAddAccountEmail(email);
  };

  const upserSubIntityInfo = async (
    subEntityId,
    aadObjectId,
    email,
    primaryEmail
  ) => {
    const payload = {
      additionalInfo: { subEntityId: subEntityId || "" },
    };
    try {
      const data = await upsertUserDetails(
        email,
        aadObjectId,
        payload,
        primaryEmail
      );
    } catch (err) {
      console.error("Failed to fetch user details:", err);
    }
  };

  const fetchV1RoasterInfo = async (aadObjectId) => {
    if (!aadObjectId) {
      return null;
    }

    const v1TableKey = process.env.REACT_APP_V1_STORAGE_ACC_ACCESS_KEY;
    const v1AccountName = process.env.REACT_APP_V1_STORAGE_ACC_NAME;

    try {
      const entity = {
        aadObjectId,
      };
      const response = await triggerOperation(
        azureTables.MERLINASSISTROASTERINFOV1,
        tableActions.GET,
        entity,
        v1TableKey,
        v1AccountName
      );

      if (response?.resp) {
        const roasterData = response.resp;
    
        setV1V2Flag(roasterData?.maversion);
        
        // Extract only the required fields for upsert
        const extractedData = {
          roasterObject: roasterData?.roasterObject,
          conversationReference: roasterData?.conversationReference,
          activityId: roasterData?.activityId
        };
        
        return extractedData;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch V1 roaster info:", error);
      return null;
    }
  };

  const updateRoasterInfo = async (
    subEntityId,
    aadObjectId,
    email,
    primaryEmail,
    roasterData = null
  ) => {
    if (!aadObjectId || !email) {
      return;
    }
    if (isUserDBSelectionEnabled()) {
      return upserSubIntityInfo(subEntityId, aadObjectId, email, primaryEmail);
    }

    try {
      const entity = {
        aadObjectId,
        //email,
        subEntityId: subEntityId ? subEntityId : "",
      };

      // Include only the extracted roaster data fields if provided
      if (roasterData) {
        
        const { roasterObject, conversationReference, activityId } = roasterData;

        if (roasterObject) entity.roasterObject = roasterObject;
        if (conversationReference) entity.conversationReference = conversationReference;
        if (activityId) entity.activityId = activityId;
        
      }

      const response = await triggerOperation(
        azureTables.MERLINASSISTROASTERINFO,
        tableActions.UPSERT,
        entity
      ); // update roaster info

      return response;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const updateV2version = async (aadObjectId, email, primaryEmail) => {
    if (!aadObjectId || !email) {
      return;
    }
    const v1TableKey = process.env.REACT_APP_V1_STORAGE_ACC_ACCESS_KEY;
    const v1AccountName = process.env.REACT_APP_V1_STORAGE_ACC_NAME;

    try {
      const entity = {
        aadObjectId,
        //email,
        maversion: 2,
      };
      const response = await triggerOperation(
        azureTables.MERLINASSISTROASTERINFOV1,
        tableActions.UPSERT,
        entity,
        v1TableKey,
        v1AccountName
      ); // update roaster info

      return response;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const initializeTeams = useCallback(async () => {
    try {
      await app.initialize();
      const context = await app.getContext();
      const usercontextData = context?.user;
      setUserContext({ ...usercontextData, fromTeams: true });
      const subEntityId = context?.page?.subPageId;

      const email =
        context?.user?.displayName || context?.user?.userPrincipalName;
      const aadObjectId = context?.user?.id;

      if (subEntityId === "updateV2") {
        await updateV2version(aadObjectId, email, email);
      }

      // Check if V1/V2 flag is already set, if not fetch it
      let roasterData = null;
      if (!roasterData) {
        roasterData = await fetchV1RoasterInfo(aadObjectId);
      }

      // Only call updateRoasterInfo if we have roaster data or need to update

         updateRoasterInfo(subEntityId, aadObjectId, email, email, roasterData);
      

      setLoadingTeamsContext(false);
      setErrorTeamsContext(null);
      setTheme(context?.app?.theme);
      // Register Teams theme change listener
      app.registerOnThemeChangeHandler((newTheme) => {
        console.log("Teams theme changed to:", newTheme);
        setTheme(newTheme || "default");
      });
      app.notifySuccess();
    } catch (err) {
      console.error("Error initializing Microsoft Teams SDK:", err);
      app.notifyFailure({ message: "Initialization failed" });
      const ctx = isUserDBSelectionEnabled()
        ? contextfromCookie()
        : contextfromLocalstore();
      const tokenId = getCookie(keys.SAAS_COMMON_BASE_TOKEN_ID);
      if (ctx && tokenId) {
        setUserContext(ctx);
        setErrorTeamsContext(null);
      } else {
        setErrorTeamsContext(STRINGS_EN.ERROR);
      }
      setLoadingTeamsContext(false);

      //  setUserContext(manualData); // TO DO to run in browser enter your details to above manualData
      // app.notifySuccess();
    }
  }, []);

  useEffect(() => {
    if (!Object.keys(userContext).length > 0) {
      initializeTeams();
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        ...userContext,
        errorTeamsContext,
        loadingTeamsContext,
        updateAccountEmail,
        addAccountEmail,
        updateTeamsContext,
        theme,
        v1V2Flag,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
